import { useEffect, useState, useCallback } from 'react';
import { gameApi } from '../services/api';
import { Player } from '../types';
import Pager from '../components/Pager';

interface LeaderboardData {
  items: Player[];
  total: number;
  page: number;
  pageSize: number;
  myRank: number; // 1-based; 0 when the caller isn't ranked (e.g. an admin)
}

export default function Leaderboard() {
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // page === undefined on the initial load → the server opens the page the current
  // player sits on (or page 1 if they aren't ranked).
  const load = useCallback(async (page?: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await gameApi.getLeaderboard(page);
      setData(response.data as LeaderboardData);
    } catch {
      setError('Failed to load leaderboard');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getRankClass = (rank: number) => {
    if (rank === 1) return 'rank-1';
    if (rank === 2) return 'rank-2';
    if (rank === 3) return 'rank-3';
    return '';
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div>
      <h1 style={{ color: 'var(--primary-color)', marginBottom: '1.5rem' }}>
        Global Leaderboard
      </h1>

      {isLoading && !data ? (
        <div className="loading">Loading leaderboard...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : data && (
        <div className="card">
          <div className="table-wrap">
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Hacker</th>
                <th>Level</th>
                <th>Reputation</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                    No players on the leaderboard yet
                  </td>
                </tr>
              ) : (
                data.items.map((player, index) => {
                  const rank = (data.page - 1) * data.pageSize + index + 1;
                  const isMe = data.myRank > 0 && rank === data.myRank;
                  return (
                    <tr
                      key={player.id}
                      style={isMe ? { background: 'rgba(0, 255, 65, 0.12)', outline: '1px solid var(--primary-color)' } : undefined}
                    >
                      <td className={getRankClass(rank)}>
                        <strong>#{rank}</strong>
                      </td>
                      <td>{player.username}{isMe && <span style={{ color: 'var(--primary-color)' }}> (you)</span>}</td>
                      <td>{player.level}</td>
                      <td>{player.reputation.toLocaleString()}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
          <Pager page={data.page} totalPages={totalPages} onPage={load} disabled={isLoading} />
        </div>
      )}
    </div>
  );
}
